import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  Autorenew,
  HourglassEmpty,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { translationService } from "../../services/translation/TranslationService";
import { parseServerDate } from "../../helpers/DateHelpers";

export type ProcessState = "pending" | "running" | "success" | "failed";

export interface ProcessStep {
  /** Stable id, e.g. "conversion" | "ocr" | "analysis" | "segmentation". */
  key: string;
  /** Already-translated label. */
  label: string;
  state: ProcessState;
  /** Optional reason shown for a failed step (e.g. the backend error message). */
  detail?: string;
  /**
   * When the backend actually started this step (ISO UTC string or Date). Drives the elapsed
   * counter, so it keeps counting across a page refresh instead of restarting at 0:00.
   */
  startedAt?: string | Date;
}

export interface ProcessProgressTrackerProps {
  steps: ProcessStep[];
  /** Translation key for the header title. */
  titleKey?: string;
  /** Render nothing once every step is success/failed (default true). */
  hideWhenAllDone?: boolean;
  /** Header toggles a Collapse (default true). */
  collapsible?: boolean;
}

function StatusIcon({ state }: { state: ProcessState }) {
  switch (state) {
    case "success":
      return <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />;
    case "failed":
      return <ErrorIcon sx={{ color: "error.main", fontSize: 20 }} />;
    case "running":
      return (
        <Autorenew
          sx={{
            color: "primary.main",
            fontSize: 20,
            animation: "esicProcSpin 1.2s linear infinite",
            "@keyframes esicProcSpin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        />
      );
    default:
      return <HourglassEmpty sx={{ color: "text.disabled", fontSize: 20 }} />;
  }
}

function defaultStatusText(state: ProcessState, t: (k: string) => string): string {
  switch (state) {
    case "pending":
      return t("ocrService.progress.state.pending");
    case "running":
      return t("ocrService.progress.state.running");
    case "success":
      return t("ocrService.progress.state.success");
    case "failed":
      return t("ocrService.progress.state.failed");
  }
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

// A backend start that lies further in the future than this is browser/server clock skew, not a
// real timestamp — the client-observed start is used instead, since a future start would otherwise
// freeze the counter at 0:00 for as long as the skew lasts.
const CLOCK_SKEW_TOLERANCE_MS = 60_000;

// Honest, state-based progress feedback for one or more processes running in parallel.
// The backend exposes no percentage/ETA, so each running step shows an indeterminate
// bar plus a live elapsed-time counter — real motion, no fabricated numbers. The caller
// computes `steps` from its service getters; this component is purely presentational.
export default observer(function ProcessProgressTracker({
  steps,
  titleKey = "ocrService.progress.title",
  hideWhenAllDone = true,
  collapsible = true,
}: ProcessProgressTrackerProps) {
  const t = (k: string) => translationService.translate(k);
  const [expanded, setExpanded] = useState(true);
  // Elapsed is measured from the backend's own start timestamp (step.startedAt) whenever the
  // caller has one: that is the same instant no matter when the page was opened, so refreshing
  // mid-run resumes the counter where it stood instead of restarting at 0:00.
  // The client-observed first-seen time below is only the fallback, for the moment between
  // pressing Run and the first status load — and for files processed before the backend
  // recorded timings, where counting from first observation is the honest best available.
  const observedStarts = useRef<Map<string, number>>(new Map());
  const [now, setNow] = useState(() => Date.now());

  // Record a first-seen time when a step begins running; clear it when it stops.
  useEffect(() => {
    const map = observedStarts.current;
    const runningKeys = new Set(steps.filter((s) => s.state === "running").map((s) => s.key));
    runningKeys.forEach((key) => {
      if (!map.has(key)) map.set(key, Date.now());
    });
    Array.from(map.keys()).forEach((key) => {
      if (!runningKeys.has(key)) map.delete(key);
    });
  });

  const startOf = (step: ProcessStep): number | undefined => {
    const backendStart = parseServerDate(step.startedAt);
    if (backendStart !== undefined && backendStart <= Date.now() + CLOCK_SKEW_TOLERANCE_MS)
      return backendStart;
    return observedStarts.current.get(step.key);
  };

  const anyRunning = steps.some((s) => s.state === "running");
  // Tick once a second while anything runs so the elapsed counters advance.
  useEffect(() => {
    if (!anyRunning) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [anyRunning]);

  if (steps.length === 0) return null;
  if (hideWhenAllDone && steps.every((s) => s.state === "success" || s.state === "failed")) return null;

  const activeCount = steps.filter((s) => s.state === "running" || s.state === "pending").length;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "primary.main",
          color: "white",
          px: 2,
          py: 1,
          cursor: collapsible ? "pointer" : "default",
        }}
        onClick={collapsible ? () => setExpanded((v) => !v) : undefined}
      >
        <Typography variant="subtitle2">
          {t(titleKey)}
          {activeCount > 0 ? ` (${activeCount})` : ""}
        </Typography>
        {collapsible && (
          <IconButton size="small" sx={{ color: "white" }}>
            {expanded ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        )}
      </Box>

      <Collapse in={!collapsible || expanded}>
        <List dense sx={{ py: 0 }}>
          {steps.map((step) => {
            const start = startOf(step);
            const secondary =
              step.state === "running" && start !== undefined
                ? `${defaultStatusText(step.state, t)} · ${formatElapsed(now - start)}`
                : step.state === "failed" && step.detail
                ? step.detail
                : defaultStatusText(step.state, t);
            return (
              <ListItem
                key={step.key}
                sx={{ flexDirection: "column", alignItems: "stretch", py: 1, px: 2 }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <StatusIcon state={step.state} />
                  <ListItemText
                    primary={step.label}
                    primaryTypographyProps={{ variant: "body2" }}
                    secondary={secondary}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </Box>
                {step.state === "running" && (
                  <LinearProgress sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />
                )}
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </Paper>
  );
});
